/**
 * Роль, которую участник «Астана дарыны» видит у себя под именем.
 *
 * Контракт:
 *  - педагоги регистрируются на ту же олимпиаду, что и ученики, и приходят той же
 *    группой `student`. Группа с бэкенда их не различает, поэтому в сайдбаре и в
 *    шторке профиля у преподавателя стояло «Студент»;
 *  - признак педагога — год рождения из ИИН раньше 2008-го. ИИН лежит в `username`:
 *    им участник логинится, туда его пишут схемы регистрации astana-daryn;
 *  - почему не по профилю. Признаки преподавателя в проекте уже есть — диапазон в
 *    astana_daryn_class_name («2,4», «5,11») и заглушка "none" в поле преподавателя,
 *    — но их ставит только ссылка ПедОлимпа. Педагоги, зарегистрированные по
 *    ученической ссылке, неотличимы от учеников, и ИИН — единственное, что у них
 *    есть. Поэтому здесь считается по ИИН, а те два признака остаются там, где и
 *    были: в отборе заданий (useDarynClassFilter) и в Ф.И.О. преподавателя
 *    (astanaDarynTeacher);
 *  - гейт — плоские ключи astana_daryn_* в additional. В остальных школах роль
 *    считается как раньше;
 *  - правило меняет только подпись на экране: права, доступ к заданиям и отбор по
 *    классу работают по-прежнему от группы и от astana_daryn_class_name.
 */

import { getParticipantRoleLabel } from "@/app/assignments/components/sections/ParticipantsSection/utils";
import { isAstanaDarynProfile } from "@/components/user-profile/astanaDarynTeacher";
import type { UserEntity } from "@/types/canonical";

/** Год рождения, начиная с которого участник считается школьником. */
const SCHOOLCHILD_BIRTH_YEAR_FROM = 2008;

/* ИИН — ГГММДД + цифра века и пола + 5 цифр: 1/2 — XIX век, 3/4 — XX, 5/6 — XXI. */
const IIN_PATTERN = /^(\d{2})(?:0[1-9]|1[0-2])\d{2}([1-6])\d{5}$/;

const CENTURY_BY_DIGIT: Record<string, number> = {
  "1": 1800,
  "2": 1800,
  "3": 1900,
  "4": 1900,
  "5": 2000,
  "6": 2000,
};

/**
 * Год рождения из ИИН, либо null.
 *
 * В username может лежать логин или почта: их отсекает шаблон целиком, поэтому
 * случайные 12 цифр не сделают ученика преподавателем.
 */
export const readIinBirthYear = (username?: string | null): number | null => {
  const match = (username ?? "").trim().match(IIN_PATTERN);

  return match ? CENTURY_BY_DIGIT[match[2]] + Number(match[1]) : null;
};

/** Участник «Астана дарыны», которого по ИИН надо показывать преподавателем. */
export const isAstanaDarynTeacher = (
  user: Pick<UserEntity, "username" | "additional">
): boolean => {
  if (!isAstanaDarynProfile(user.additional)) return false;

  const birthYear = readIinBirthYear(user.username);

  return birthYear !== null && birthYear < SCHOOLCHILD_BIRTH_YEAR_FROM;
};

/** Ключ i18n для роли, которую пользователь видит у себя в профиле. */
export const getOwnRoleLabel = (user: UserEntity): string =>
  isAstanaDarynTeacher(user)
    ? "label-teacher"
    : getParticipantRoleLabel(user as any);
